import { useEffect, useId, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { extractErrorMessage } from '../api/client';

/** Lo mínimo que muestra la lista: SKU, descripción y si está activo (sin costos ni precios). */
export interface SkuSugerido {
  skuInterno: string;
  descripcion: string | null;
  /** false = discontinuado: se muestra con la marca "inactivo". */
  activo?: boolean;
}

/** Con menos caracteres no se busca (el catálogo es grande). */
export const MIN_CARACTERES_SKU = 2;

type Estado = 'inactivo' | 'buscando' | 'listo' | 'error';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown' | 'role'> & {
  value: string;
  /** Lo que se escribe (texto libre: también se puede cargar un SKU sin elegirlo de la lista). */
  onChange: (texto: string) => void;
  /** Un SKU elegido de la lista (mouse o Enter). */
  onElegir: (sku: SkuSugerido) => void;
  onBlur?: () => void;
  /** Busca por texto; tiene que ser estable (una función de módulo o memorizada). */
  buscar: (texto: string) => Promise<SkuSugerido[]>;
  demoraMs?: number;
  'data-falta'?: string;
};

/**
 * Campo de SKU con sugerencias (combobox): se escribe libremente y, desde 2 caracteres, muestra los SKU que coinciden.
 * Flechas para moverse, Enter elige, Escape cierra (sin cerrar la ventana de abajo); clic afuera también cierra.
 */
export function BuscadorSku({
  value, onChange, onElegir, onBlur, buscar, demoraMs = 250, className, id, ...resto
}: Props) {
  const idPropio = useId();
  const idCampo = id ?? `sku-${idPropio}`;
  const idLista = `${idCampo}-lista`;
  const raiz = useRef<HTMLDivElement>(null);
  const [abierto, setAbierto] = useState(false);
  const [resultados, setResultados] = useState<SkuSugerido[]>([]);
  const [estado, setEstado] = useState<Estado>('inactivo');
  const [error, setError] = useState('');
  const [activo, setActivo] = useState(-1);
  const texto = useDebounce(value.trim(), demoraMs);

  // Busca el texto (ya sin tipear) mientras la lista está abierta; una respuesta de un texto anterior se descarta.
  useEffect(() => {
    if (!abierto || texto.length < MIN_CARACTERES_SKU) {
      setResultados([]); setEstado('inactivo'); setActivo(-1);
      return;
    }
    let vigente = true;
    setEstado('buscando');
    buscar(texto).then(
      r => { if (vigente) { setResultados(Array.isArray(r) ? r : []); setActivo(-1); setEstado('listo'); } },
      e => { if (vigente) { setResultados([]); setActivo(-1); setError(extractErrorMessage(e)); setEstado('error'); } },
    );
    return () => { vigente = false; };
  }, [texto, abierto, buscar]);

  useEffect(() => {
    if (!abierto) return;
    const alClic = (e: MouseEvent) => { if (!raiz.current?.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('mousedown', alClic);
    return () => document.removeEventListener('mousedown', alClic);
  }, [abierto]);

  const idOpcion = (i: number) => `${idLista}-${i}`;
  useEffect(() => {
    if (activo >= 0) document.getElementById(idOpcion(activo))?.scrollIntoView?.({ block: 'nearest' });
  });

  const visible = abierto && value.trim().length >= MIN_CARACTERES_SKU && estado !== 'inactivo';
  const conOpciones = visible && estado === 'listo' && resultados.length > 0;

  const elegir = (s: SkuSugerido) => {
    onElegir(s);
    setAbierto(false);
    setActivo(-1);
  };

  const alTeclear = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = resultados.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!abierto) { setAbierto(true); return; }
        if (conOpciones) setActivo(i => (i + 1) % n);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (conOpciones) setActivo(i => (i <= 0 ? n - 1 : i - 1));
        return;
      case 'Enter':
        if (conOpciones && activo >= 0) { e.preventDefault(); elegir(resultados[activo]); }
        return;
      case 'Escape':
        if (abierto) { e.preventDefault(); e.stopPropagation(); setAbierto(false); setActivo(-1); }
        return;
    }
  };

  return (
    <div ref={raiz} className="relative">
      <input
        {...resto}
        id={idCampo}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={conOpciones ? idLista : undefined}
        aria-activedescendant={conOpciones && activo >= 0 ? idOpcion(activo) : undefined}
        value={value}
        onChange={e => { onChange(e.target.value); setAbierto(true); setActivo(-1); }}
        onKeyDown={alTeclear}
        onBlur={() => { setAbierto(false); onBlur?.(); }}
        className={className ?? 'w-full px-3 py-2 border rounded-lg'}
      />
      {visible && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
          {estado === 'buscando' && <p className="px-3 py-2 text-slate-500">Buscando…</p>}
          {estado === 'error' && (
            <p role="alert" className="px-3 py-2 text-red-600">No se pudo buscar el SKU: {error}</p>
          )}
          {estado === 'listo' && resultados.length === 0 && <p className="px-3 py-2 text-slate-500">Sin resultados</p>}
          {conOpciones && (
            <ul id={idLista} role="listbox" aria-label="SKU sugeridos">
              {resultados.map((s, i) => (
                <li
                  key={s.skuInterno}
                  id={idOpcion(i)}
                  role="option"
                  aria-selected={i === activo}
                  // Sin perder el foco del campo: elegir no cuenta como salir de él.
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => elegir(s)}
                  onMouseEnter={() => setActivo(i)}
                  className={`px-3 py-2 cursor-pointer ${i === activo ? 'bg-brand-50 text-brand-700' : 'hover:bg-slate-50'}`}
                >
                  <span className="font-mono">{s.skuInterno}</span>
                  {s.descripcion ? ` — ${s.descripcion}` : ''}
                  {s.activo === false && <span className="ml-1 text-xs text-amber-700"> (inactivo)</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
